import { useState } from "react";
import type { GoalDetail } from "web_myplann/Goals/Types/GoalDetail";
import { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";

type LoanConfig = {
	rate: number;
	termYears: number;
	termMin: number;
	termMax: number;
};

const MORTGAGE_DEFAULTS: LoanConfig = {
	rate: 4.5,
	termYears: 25,
	termMin: 5,
	termMax: 40,
};

const LOAN_DEFAULTS: LoanConfig = {
	rate: 6,
	termYears: 5,
	termMin: 1,
	termMax: 10,
};

export const formatRate = (rate: number): string =>
	rate.toFixed(1).replace(".", ",");

type LoanTotals = {
	monthlyPayment: number;
	totalPaid: number;
	overpay: number;
};

const computeLoanTotals = (
	loanAmount: number,
	annualRatePercent: number,
	termYears: number,
): LoanTotals => {
	const r = annualRatePercent / 100 / 12;
	const n = termYears * 12;
	if (n <= 0) {
		return { monthlyPayment: 0, totalPaid: 0, overpay: 0 };
	}
	const exactMonthly =
		r === 0
			? loanAmount / n
			: (loanAmount * r * (1 + r) ** n) / ((1 + r) ** n - 1);
	const monthlyPayment = Math.round(exactMonthly);
	const totalPaid = monthlyPayment * n;
	const overpay = Math.max(0, totalPaid - loanAmount);
	return { monthlyPayment, totalPaid, overpay };
};

type UseLoanCalculatorResult = {
	isMortgage: boolean;
	rate: number;
	setRate: (value: number) => void;
	termYears: number;
	setTermYears: (value: number) => void;
	termMin: number;
	termMax: number;
	collapsed: boolean;
	toggleCollapsed: () => void;
	monthlyPayment: number;
	totalPaid: number;
	overpay: number;
};

export const useLoanCalculator = (
	goal: GoalDetail,
): UseLoanCalculatorResult => {
	const isMortgage = goal.category === GoalCategoryTypeEnum.House;
	const defaults = isMortgage ? MORTGAGE_DEFAULTS : LOAN_DEFAULTS;

	const [rate, setRate] = useState<number>(defaults.rate);
	const [termYears, setTermYears] = useState<number>(defaults.termYears);
	const [collapsed, setCollapsed] = useState<boolean>(true);

	const { monthlyPayment, totalPaid, overpay } = computeLoanTotals(
		goal.loanAmount,
		rate,
		termYears,
	);

	return {
		isMortgage,
		rate,
		setRate,
		termYears,
		setTermYears,
		termMin: defaults.termMin,
		termMax: defaults.termMax,
		collapsed,
		toggleCollapsed: () => setCollapsed((c) => !c),
		monthlyPayment,
		totalPaid,
		overpay,
	};
};
